import { type QuotationStep } from "@/features/modals/QuotationsModal";
import { createQuotation, deleteQuotation, updateQuotation } from "@repo/api";
import { Button } from "@repo/skapa";
import { t } from "@repo/translation";
import { FlexDiv, useToast } from "@repo/ui";
import { getDebugMessage, getErrorMessage } from "@repo/utils";
import { useCallback, useState } from "react";

interface ProjectQuotationButtonProps {
  step: QuotationStep;
  setStep: (step: QuotationStep) => void;
  quotationId: string;
  quotationTitle: string;
  opportunityId?: number;
  oldQuotationId?: string;
  oldQuotationTitle?: string;
}

export const ProjectQuotationButton = ({
  step,
  setStep,
  quotationId,
  quotationTitle,
  opportunityId,
  oldQuotationId,
  oldQuotationTitle,
}: ProjectQuotationButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  let text = "";
  let nextStep: QuotationStep;
  const { updateToast } = useToast();

  if (step === "MANAGE") {
    text = t("action.update");
    nextStep = "OVERVIEW";
  } else if (step === "NEW") {
    text = t("action.submit_quotation");
    nextStep = "OVERVIEW";
  } else if (step === "OVERVIEW") {
    text = t("action.associate_quotation");
    nextStep = "NEW";
  }

  const handleDelete = useCallback(() => {
    setIsLoading(true);
    if (opportunityId && quotationId) {
      deleteQuotation(opportunityId, quotationId)
        .then(() => {
          updateToast({
            id: quotationId,
            title: t("prompt.quotation_deleted"),
          });
          setStep(nextStep);
        })
        .catch((e) => {
          updateToast({
            id: quotationId,
            title: t("prompt.quotation_deleted_failed"),
            message: getDebugMessage(e),
            type: "error",
          });
        });
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunityId, quotationId, updateToast, setStep]);

  const handleSave = useCallback(async () => {
    setIsLoading(true);

    if (step === "NEW" || step === "MANAGE") {
      try {
        if (opportunityId && oldQuotationId) {
          await updateQuotation(
            opportunityId,
            oldQuotationId,
            quotationId,
            quotationTitle
          );
        } else if (opportunityId) {
          await createQuotation(opportunityId, quotationId, quotationTitle);
        }
        updateToast({
          id: quotationId,
          title: t("prompt.quotation_saved"),
        });
        setStep(nextStep);
      } catch (err) {
        updateToast({
          id: quotationId,
          title: t("prompt.quotation_saved_failed"),
          message: getErrorMessage(err),
          type: "error",
        });
      }
    } else {
      setStep(nextStep);
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    step,
    opportunityId,
    oldQuotationId,
    quotationId,
    quotationTitle,
    oldQuotationTitle,
    updateToast,
    setStep,
  ]);

  return (
    <FlexDiv flexDirection="row" gap="10px">
      {step === "MANAGE" && (
        <Button
          onClick={handleDelete}
          type="secondary"
          text={t("action.delete")}
          loading={isLoading}
        />
      )}
      <Button
        onClick={handleSave}
        type="primary"
        text={text}
        loading={isLoading}
        disabled={
          (step === "MANAGE" || step === "NEW") &&
          (!quotationId || !quotationTitle)
        }
      />
    </FlexDiv>
  );
};
